import { MANAGER_SOCIALS } from "../../config/league";

/** YouTube + X links for a manager, if configured. Renders nothing otherwise. */
export function SocialLinks({
  name,
  size = "sm",
  className = "",
}: {
  name: string;
  size?: "sm" | "md";
  className?: string;
}) {
  const s = MANAGER_SOCIALS[name];
  if (!s || (!s.youtube && !s.x && !s.seekingAlpha && !s.website)) return null;
  const dim = size === "md" ? "h-5 w-5" : "h-4 w-4";
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      {s.youtube && (
        <a
          href={s.youtube}
          target="_blank"
          rel="noreferrer noopener"
          onClick={(e) => e.stopPropagation()}
          aria-label={`${name} on YouTube`}
          title={`${name} on YouTube`}
          className="text-faint transition hover:text-[#FF0000]"
        >
          <YouTubeIcon className={dim} />
        </a>
      )}
      {s.x && (
        <a
          href={s.x}
          target="_blank"
          rel="noreferrer noopener"
          onClick={(e) => e.stopPropagation()}
          aria-label={`${name} on X`}
          title={`${name} on X`}
          className="text-faint transition hover:text-fg"
        >
          <XIcon className={dim} />
        </a>
      )}
      {s.seekingAlpha && (
        <a
          href={s.seekingAlpha}
          target="_blank"
          rel="noreferrer noopener"
          onClick={(e) => e.stopPropagation()}
          aria-label={`${name} on Seeking Alpha`}
          title={`${name} on Seeking Alpha`}
          className="text-faint transition hover:text-[#FF6600]"
        >
          <SeekingAlphaIcon className={dim} />
        </a>
      )}
      {s.website && (
        <a
          href={s.website}
          target="_blank"
          rel="noreferrer noopener"
          onClick={(e) => e.stopPropagation()}
          aria-label={`${name}'s website`}
          title={`${name}'s website`}
          className="text-faint transition hover:text-accent"
        >
          <GlobeIcon className={dim} />
        </a>
      )}
    </span>
  );
}

function YouTubeIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z" />
    </svg>
  );
}

function XIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.9 1.5h3.7l-8 9.1L24 22.5h-7.4l-5.8-7.6-6.6 7.6H.5l8.6-9.8L0 1.5h7.6l5.2 6.9 6.1-6.9zm-1.3 18.8h2L6.5 3.6H4.4L17.6 20.3z" />
    </svg>
  );
}

function SeekingAlphaIcon({ className = "" }: { className?: string }) {
  // Seeking Alpha has no simple mark; use its signature "α" glyph.
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <text
        x="12"
        y="19"
        textAnchor="middle"
        fontSize="22"
        fontWeight="700"
        fontFamily="Georgia, 'Times New Roman', serif"
        fill="currentColor"
      >
        α
      </text>
    </svg>
  );
}

function GlobeIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9.5" />
      <path d="M2.5 12h19" />
      <path d="M12 2.5c2.7 2.8 4.2 6 4.2 9.5s-1.5 6.7-4.2 9.5c-2.7-2.8-4.2-6-4.2-9.5S9.3 5.3 12 2.5z" />
    </svg>
  );
}
